import * as React from 'react';
import { useLocation, useRoute } from 'wouter';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import OnlineHelp from '../../OnlineHelp';
import { CmdTable } from '../../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import { CUSTOMERS_KEY, REQUIRED } from '../../CmdTable/constants';
import {
  EXPENSE_TYPES_ACTIVE_KEY,
  EXPENSE_TYPES_DELETED_AT_KEY,
  EXPENSE_TYPES_DETAIL_KEY,
  EXPENSE_TYPES_NAME_KEY,
  EXPENSE_TYPES_TABLE_KEY,
  EXPENSE_TYPES_TABLE_SETTINGS,
  TableSettingsKeys,
} from './constants';
import CmdTableSettings from '../../CmdTable/settings/CmdTableSettings';
import FilterChips from '../../CmdTable/FilterChips/FilterChips';
import { TExpenseTypesRecords, TExpenseTypesResponse } from './types';
import useUrlQueryParams from '../../../hooks/useUrlQueryParams';
import useExpenseTypes from './useExpenseTypes';
import PermissionsService, { PERMISSION_EXPENSE_TYPE } from '../../../Services/PermissionsService';
import { URL_EXPENSE_TYPES } from '../../../router/constants';
import OperatingCostTypeForm from './OperatingCostTypeForm';
import OperatingCostTypeDetail from './OperatingCostTypeDetail';
import { customersAtom, selectedCustomersAtom } from '../../../store/recoil/customers';
import { arrayOrder, getTableName, getTableSettings } from '../../CmdTable/CmdTableService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../../CmdTable/CmdTableState';
import useAnalytics from '../../../hooks/useAnalytics';
import PageHeader from '../../PageHeader';

export default function OperatingCostTypes() {
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_EXPENSE_TYPES}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [id, setId] = React.useState<number | null>(null);
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(EXPENSE_TYPES_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(EXPENSE_TYPES_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(EXPENSE_TYPES_TABLE_KEY)
  );
  const query = useUrlQueryParams();
  const customerIdUrl = query.get('customerId');
  const { columns, columnOrder, columnVisibility } = useExpenseTypes();
  const { trackEvent } = useAnalytics();

  const handleClose = React.useCallback(() => {
    trackEvent([`table ${getTableName(EXPENSE_TYPES_TABLE_KEY)}`, 'click', 'Close modal']);
    navigate(`${URL_EXPENSE_TYPES}`);
    setIsModalOpened(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [EXPENSE_TYPES_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  const handlePathData = (documentTitle: string, typeId?: number, customerValue?: number) => {
    setIsModalOpened(true);
    setId(typeId || null);
    setCustomerId(customerValue || null);
    setDocumentTitle(documentTitle);
  };

  React.useEffect(() => {
    if (pathname === `${URL_EXPENSE_TYPES}`) {
      const documentTitle = `${f({
        id: 'menu.expenseTypesOverview',
      })} - ${f({ id: 'menu.costs' })}`;
      setDocumentTitle(documentTitle);
    }
    if (pathname === `${URL_EXPENSE_TYPES}/add`) {
      const documentTitle = `${f({ id: 'table.add' })} - ${f({
        id: 'menu.expenseTypesOverview',
      })} - ${f({ id: 'menu.costs' })}`;
      handlePathData(documentTitle);
    } else if (params) {
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({
        id: 'menu.expenseTypesOverview',
      })} - ${f({ id: 'menu.costs' })}`;
      handlePathData(documentTitle, Number(params.id), Number(customerIdUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, navigate, pathname]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(EXPENSE_TYPES_TABLE_KEY);
    if (settings) {
      // setTableSettingsState(settings);
      // setSortFilter({ [`${settings.sort.column}`]: settings.sort.direction });
      handleSetOrderColumns(settings.columns);
      // setIsSettingsReady(true);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [`cmdTableSettings-${EXPENSE_TYPES_TABLE_KEY}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackEvent([`table ${getTableName(EXPENSE_TYPES_TABLE_KEY)}`, 'click', 'Add new record']);
    navigate(`${URL_EXPENSE_TYPES}/add`);
  };

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'menu.costs' })} / ${f({ id: 'menu.expenseTypesOverview' })}`}
        onlineHelpTopic="suppliers"
      />
      <div className="w-page mt-8">
        <div className="w-page-content">
          <div className="container-fluid">
            {/* <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} /> */}
            <section className="basic-box border-top-thin border-top-gray">
              <div
                className="basic-box__content"
                // style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}
              >
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {/* <div> */}
                    {PermissionsService.hasAccess(
                      PERMISSION_EXPENSE_TYPE,
                      ['create'],
                      selectedCustomers
                    ) && (
                      <a
                        href="#"
                        data-cy="table-record-addBtn"
                        title={f({ id: 'table.addBtn' })}
                        className="e-button e-button--gray"
                        data-id="addAlarm"
                        // style="margin-right: 7px;"
                        onClick={handleAddButton}
                      >
                        {f({ id: 'table.addBtn' })}
                      </a>
                    )}
                    {/* </div> */}
                  </div>
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={`${EXPENSE_TYPES_TABLE_KEY}`}
                      // actionColumnEnabled={true}
                      tableName={EXPENSE_TYPES_TABLE_SETTINGS}
                      sort={{ column: EXPENSE_TYPES_NAME_KEY, direction: 'desc' }}
                      translationKeys={TableSettingsKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips
                        key={filterId}
                        id={filterId}
                        tableName={EXPENSE_TYPES_TABLE_KEY}
                      />
                    ))}
                </div>
                {isLoadingSettings && <CmdLoader inContent={true} />}
                <CmdTable<TExpenseTypesResponse, TExpenseTypesRecords>
                  name={EXPENSE_TYPES_TABLE_KEY}
                  stickyColumns={[EXPENSE_TYPES_DETAIL_KEY, EXPENSE_TYPES_DELETED_AT_KEY]}
                  lockedColumns={[EXPENSE_TYPES_ACTIVE_KEY, EXPENSE_TYPES_NAME_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
            {isModalOpened &&
              id &&
              customerId &&
              PermissionsService.hasAccess(PERMISSION_EXPENSE_TYPE, ['update'], customerId) && (
                <OperatingCostTypeForm
                  closeCallback={handleClose}
                  selectedId={id}
                  refreshTableCallback={updateTable}
                  customerId={customerId}
                />
              )}
            {isModalOpened && !id && (
              <OperatingCostTypeForm
                closeCallback={handleClose}
                refreshTableCallback={updateTable}
                customerId={customerId}
              />
            )}
            {isModalOpened &&
              id &&
              (!customerId ||
                !PermissionsService.hasAccess(PERMISSION_EXPENSE_TYPE, ['update'], customerId)) && (
                <OperatingCostTypeDetail
                  closeCallback={handleClose}
                  id={id}
                  customerId={customerId}
                />
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
